import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fetchPrintFailures, USING_MOCK_PRINT_FAILURES } from "../../utils/printFailuresService";
import {
  DOCKET_TYPES,
  ORIGINS,
  REASONS,
  SEVERITY,
  STEP_STATUS_LABELS,
  buildSupportText,
  durationText,
  eventLabel,
  formatClock,
  formatDay,
  isResolved,
  printerWasOnline,
  reasonMeta,
  timeAgo,
} from "../../utils/printFailureReasons";
import "../../styles/failedPrints.css";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
];

const REFRESH_MS = 30000;

const dayHeading = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return formatDay(iso);
};

const Chip = ({ active, onClick, children, count }) => (
  <button type="button" className={`fpChip ${active ? "active" : ""}`} onClick={onClick}>
    {children}
    {count !== undefined && <span className="fpChipCount">{count}</span>}
  </button>
);

const StatCard = ({ value, label, tone, onClick, active }) => (
  <button
    type="button"
    className={`fpStat ${tone || ""} ${active ? "active" : ""}`}
    onClick={onClick}
    disabled={!onClick}
  >
    <span className="fpStatValue">{value}</span>
    <span className="fpStatLabel">{label}</span>
  </button>
);

const Timeline = ({ steps }) => (
  <ol className="fpTimeline">
    {steps.map((s) => (
      <li key={s.key} className={`fpStep ${s.status}`}>
        <span className="fpStepDot" aria-hidden="true" />
        <div className="fpStepBody">
          <div className="fpStepHead">
            <span className="fpStepLabel">{s.label}</span>
            <span className="fpStepStatus">{STEP_STATUS_LABELS[s.status] || s.status}</span>
          </div>
          {s.note && <div className="fpStepNote">{s.note}</div>}
        </div>
      </li>
    ))}
  </ol>
);

const Fact = ({ label, children }) => (
  <div className="fpFact">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const FailureCard = ({ f, expanded, onToggle, siblings, onFilterOrder, now }) => {
  const reason = reasonMeta(f.reason_code);
  const resolved = isResolved(f);
  const sev = SEVERITY[reason.severity];
  const origin = ORIGINS[f.origin] || { label: f.origin, className: "" };
  const docket = DOCKET_TYPES[f.docket_type]?.label || f.docket_type;

  const copySupport = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildSupportText(f));
      toast.success("Details copied — paste them to the dev team");
    } catch (err) {
      toast.error("Could not copy. Select the text manually.");
    }
  };

  return (
    <article className={`fpCard ${sev.className} ${resolved ? "resolved" : ""} ${expanded ? "open" : ""}`}>
      <div
        className="fpCardHead"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="fpTop">
          <span className={`fpSev ${sev.className}`}>{sev.label}</span>
          {resolved && (
            <span className="fpResolved" title="A later print of this docket went through">
              Reprinted OK {formatClock(f.resolved_at)}
            </span>
          )}
          <h4 className="fpTitle">{reason.title}</h4>
          <span className="fpTime">
            {formatClock(f.occurred_at)} <span className="fpAgo">· {timeAgo(f.occurred_at, now)}</span>
          </span>
          <span className={`fpChevron ${expanded ? "open" : ""}`} aria-hidden="true">
            ▾
          </span>
        </div>

        <div className="fpMeta">
          <span className={`fpOrigin ${origin.className}`}>{origin.label}</span>
          <span className="fpType">{docket} docket</span>
          {f.printer ? (
            <span className="fpPrinter">
              <span className={`fpLed ${printerWasOnline(f.printer) === false ? "off" : "on"}`} />
              {f.printer.name}
            </span>
          ) : (
            <span className="fpPrinter none">No printer</span>
          )}
          <span
            className="fpOrderBtn"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onFilterOrder(f.order_id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                onFilterOrder(f.order_id);
              }
            }}
            title="Show only prints for this order"
          >
            Order #{f.order_id}
          </span>
          {siblings.length > 0 && (
            <span className="fpAlso">
              Also failed:{" "}
              {siblings.map((s) => DOCKET_TYPES[s.docket_type]?.label || s.docket_type).join(", ")}
            </span>
          )}
        </div>

        <p className="fpDetail">{f.detail}</p>
      </div>

      {expanded && (
        <div className="fpBody">
          <div className="fpAction">
            <span className="fpActionLabel">What to do</span>
            <p>{reason.action}</p>
          </div>

          <div className="fpColumns">
            <section>
              <h5 className="fpSectionTitle">What happened, step by step</h5>
              <Timeline steps={f.steps} />
            </section>

            <section>
              <h5 className="fpSectionTitle">Details</h5>
              <dl className="fpFacts">
                <Fact label="Time">
                  {formatDay(f.occurred_at)}, {formatClock(f.occurred_at)}
                </Fact>
                <Fact label="Order">
                  #{f.order_id}
                  {f.parent_order_id ? ` (added to #${f.parent_order_id})` : ""}
                </Fact>
                <Fact label="Came from">{f.origin_detail}</Fact>
                <Fact label="Print type">{eventLabel(f.event)}</Fact>
                <Fact label="Printer">
                  {f.printer ? (
                    <>
                      {f.printer.name}
                      <span className="fpSub">
                        {" "}
                        · printer id {f.printer.id} · docket format {f.printer.docket_version}
                      </span>
                    </>
                  ) : (
                    "None selected"
                  )}
                </Fact>
                {f.printer && (
                  <Fact label="Printer status">
                    {f.printer.offline_since ? (
                      <span className="fpBad">
                        {printerWasOnline(f.printer) === false ? "Offline when sent, " : ""}
                        still offline after {durationText(f.printer.offline_since, now)} (since{" "}
                        {formatClock(f.printer.offline_since)})
                      </span>
                    ) : printerWasOnline(f.printer) === false ? (
                      <span className="fpBad">
                        Offline when sent{f.printer.online ? " (back online now)" : ""}
                      </span>
                    ) : f.printer.online === false ? (
                      <span className="fpBad">Offline</span>
                    ) : (
                      <span className="fpGood">Online</span>
                    )}
                  </Fact>
                )}
              </dl>

              <h5 className="fpSectionTitle">Technical details (for the dev team)</h5>
              <div className="fpTech">
                {f.technical?.message && (
                  <div className="fpTechRow">
                    <span className="fpTechLabel">Error</span>
                    <span>{f.technical.message}</span>
                  </div>
                )}
                {f.technical?.file && (
                  <div className="fpTechRow">
                    <span className="fpTechLabel">Where</span>
                    <span>
                      {f.technical.file}:{f.technical.line}
                    </span>
                  </div>
                )}
                <div className="fpTechRow">
                  <span className="fpTechLabel">Log trail</span>
                  <span>{f.technical?.logged_steps || "—"}</span>
                </div>
              </div>

              <button type="button" className="fpCopyBtn" onClick={copySupport}>
                Copy details for support
              </button>
            </section>
          </div>
        </div>
      )}
    </article>
  );
};

const FailedPrintsPanel = () => {
  const [range, setRange] = useState("today");
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [now, setNow] = useState(Date.now());

  const [originFilter, setOriginFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [printerFilter, setPrinterFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await fetchPrintFailures(range);
        setFailures(res.failures);
        setUpdatedAt(res.generatedAt);
        setError(false);
      } catch (err) {
        console.error(err);
        setError(true);
        if (!silent) toast.error("Could not load failed prints");
      } finally {
        setNow(Date.now());
        if (!silent) setLoading(false);
      }
    },
    [range]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const sorted = useMemo(
    () => [...failures].sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at)),
    [failures]
  );

  const counts = useMemo(() => {
    const by = (key) =>
      sorted.reduce((acc, f) => {
        acc[f[key]] = (acc[f[key]] || 0) + 1;
        return acc;
      }, {});
    const open = sorted.filter((f) => !isResolved(f));
    return {
      origin: by("origin"),
      type: by("docket_type"),
      critical: open.filter((f) => reasonMeta(f.reason_code).severity === "critical").length,
      warning: open.filter((f) => reasonMeta(f.reason_code).severity === "warning").length,
      resolved: sorted.length - open.length,
      orders: new Set(open.map((f) => f.order_id)).size,
    };
  }, [sorted]);

  const printerOptions = useMemo(() => {
    const map = new Map();
    sorted.forEach((f) => f.printer && map.set(String(f.printer.id), f.printer.name));
    return [...map.entries()];
  }, [sorted]);

  const reasonOptions = useMemo(
    () => [...new Set(sorted.map((f) => f.reason_code))],
    [sorted]
  );

  const visible = useMemo(
    () =>
      sorted.filter((f) => {
        if (originFilter !== "all" && f.origin !== originFilter) return false;
        if (typeFilter !== "all" && f.docket_type !== typeFilter) return false;
        if (severityFilter === "resolved") {
          if (!isResolved(f)) return false;
        } else if (severityFilter !== "all") {
          if (isResolved(f) || reasonMeta(f.reason_code).severity !== severityFilter) return false;
        }
        if (reasonFilter !== "all" && f.reason_code !== reasonFilter) return false;
        if (printerFilter === "none" && f.printer) return false;
        if (printerFilter !== "all" && printerFilter !== "none" && String(f.printer?.id) !== printerFilter)
          return false;
        if (search.trim() && !String(f.order_id).includes(search.trim().replace("#", ""))) return false;
        return true;
      }),
    [sorted, originFilter, typeFilter, severityFilter, reasonFilter, printerFilter, search]
  );

  const filtersActive =
    originFilter !== "all" ||
    typeFilter !== "all" ||
    severityFilter !== "all" ||
    reasonFilter !== "all" ||
    printerFilter !== "all" ||
    search.trim() !== "";

  const clearFilters = () => {
    setOriginFilter("all");
    setTypeFilter("all");
    setSeverityFilter("all");
    setReasonFilter("all");
    setPrinterFilter("all");
    setSearch("");
  };

  let lastDay = null;

  return (
    <div className="failedPrints">
      <div className="fpHeader">
        <div>
          <h3 className="fpHeading">Failed prints</h3>
          <p className="fpSubheading">
            Only prints that did not reach a printer. Successful prints are hidden.
          </p>
        </div>
        <div className="fpHeaderControls">
          <div className="fpRange" role="group" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`fpRangeBtn ${range === r.key ? "active" : ""}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" className="fpRefreshBtn" onClick={() => load(false)} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
          <label className="fpAuto">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
        </div>
      </div>

      {USING_MOCK_PRINT_FAILURES && (
        <div className="fpDemoBanner">
          Showing demo data. This screen is not connected to live print logs yet.
        </div>
      )}

      <div className="fpStats">
        <StatCard
          value={sorted.length}
          label="Failed prints"
          tone={sorted.length - counts.resolved ? "bad" : "good"}
          onClick={clearFilters}
        />
        <StatCard
          value={counts.critical}
          label="Not printed"
          tone={counts.critical ? "bad" : ""}
          active={severityFilter === "critical"}
          onClick={() => setSeverityFilter(severityFilter === "critical" ? "all" : "critical")}
        />
        <StatCard
          value={counts.warning}
          label="Delayed (printer offline)"
          tone={counts.warning ? "warn" : ""}
          active={severityFilter === "warning"}
          onClick={() => setSeverityFilter(severityFilter === "warning" ? "all" : "warning")}
        />
        <StatCard
          value={counts.resolved}
          label="Reprinted OK later"
          tone={counts.resolved ? "good" : ""}
          active={severityFilter === "resolved"}
          onClick={() => setSeverityFilter(severityFilter === "resolved" ? "all" : "resolved")}
        />
        <StatCard value={counts.orders} label="Orders still affected" />
      </div>

      <div className="fpFilters">
        <div className="fpFilterGroup">
          <span className="fpFilterLabel">Source</span>
          <Chip active={originFilter === "all"} onClick={() => setOriginFilter("all")}>
            All
          </Chip>
          {Object.entries(ORIGINS)
            .filter(([key]) => counts.origin[key])
            .map(([key, o]) => (
              <Chip
                key={key}
                active={originFilter === key}
                onClick={() => setOriginFilter(key)}
                count={counts.origin[key]}
              >
                {o.label}
              </Chip>
            ))}
        </div>

        <div className="fpFilterGroup">
          <span className="fpFilterLabel">Docket</span>
          <Chip active={typeFilter === "all"} onClick={() => setTypeFilter("all")}>
            All
          </Chip>
          {Object.entries(DOCKET_TYPES).map(([key, t]) => (
            <Chip
              key={key}
              active={typeFilter === key}
              onClick={() => setTypeFilter(key)}
              count={counts.type[key] || 0}
            >
              {t.label}
            </Chip>
          ))}
        </div>

        <div className="fpFilterGroup fpFilterInputs">
          <select
            className="fpSelect"
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            aria-label="Filter by reason"
          >
            <option value="all">Any reason</option>
            {reasonOptions.map((code) => (
              <option key={code} value={code}>
                {REASONS[code]?.title || code}
              </option>
            ))}
          </select>
          <select
            className="fpSelect"
            value={printerFilter}
            onChange={(e) => setPrinterFilter(e.target.value)}
            aria-label="Filter by printer"
          >
            <option value="all">Any printer</option>
            {printerOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
            <option value="none">No printer</option>
          </select>
          <input
            className="fpSearch"
            type="search"
            inputMode="numeric"
            placeholder="Find order #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Find by order number"
          />
          {filtersActive && (
            <button type="button" className="fpClearBtn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="fpListMeta">
        <span>
          Showing {visible.length} of {sorted.length}
        </span>
        {updatedAt && <span>Updated {formatClock(updatedAt)}</span>}
      </div>

      {error && !loading && (
        <div className="fpEmpty bad">
          <h4>Could not load failed prints</h4>
          <p>Check the connection, then press Refresh.</p>
        </div>
      )}

      {loading && sorted.length === 0 && !error && (
        <div className="fpSkeletons" aria-hidden="true">
          <div className="fpSkeleton" />
          <div className="fpSkeleton" />
          <div className="fpSkeleton" />
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div className="fpEmpty good">
          <div className="fpTick" aria-hidden="true">
            ✓
          </div>
          <h4>No failed prints</h4>
          <p>Every docket for this period reached a printer or its queue.</p>
        </div>
      )}

      {!loading && sorted.length > 0 && visible.length === 0 && (
        <div className="fpEmpty">
          <h4>No prints match these filters</h4>
          <button type="button" className="fpClearBtn" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      )}

      <div className="fpList">
        {visible.map((f) => {
          const heading = dayHeading(f.occurred_at);
          const showHeading = range === "7d" && heading !== lastDay;
          lastDay = heading;
          return (
            <React.Fragment key={f.id}>
              {showHeading && <div className="fpDayHeading">{heading}</div>}
              <FailureCard
                f={f}
                now={now}
                expanded={expandedId === f.id}
                onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
                siblings={sorted.filter((x) => x.order_id === f.order_id && x.id !== f.id)}
                onFilterOrder={(id) => setSearch(String(id))}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default FailedPrintsPanel;
